import type {MentionThreadContext} from '@shm/shared/models/mention-ranking'
import type {DomainResolverFn} from '@seed-hypermedia/client'
import type {EditorBlock} from '@seed-hypermedia/client/editor-types'
import {HMBlockNode, HMMetadata, UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {useCommentDraftReferences, type CommentDraftReference} from '@shm/shared/comments-service-provider'
import {blocksHaveDraftContent} from './comment-editor-draft-content'
import {hmBlocksToEditorContent} from '@seed-hypermedia/client/hmblock-to-editorblock'
import {useOpenUrl, useUniversalClient, writeableStateStream} from '@shm/shared'
import {useAccount} from '@shm/shared/models/entity'
import {useTx} from '@shm/shared/translation'
import type {UniversalClient} from '@shm/shared/universal-client'
import {UIAvatar} from '@shm/ui/avatar'
import {Button} from '@shm/ui/button'
import {LinkIcon} from '@shm/ui/hm-icon'
import {AtSignIcon, ImageIcon, SlashSquareIcon} from '@shm/ui/icons'
import {cn} from '@shm/ui/utils'
import {Extension} from '@tiptap/core'
import {Plugin, PluginKey} from '@tiptap/pm/state'
import {type MutableRefObject, useEffect, useLayoutEffect, useRef, useState} from 'react'
import avatarPlaceholder from './assets/avatar.png'
import {BlockNoteEditor, getBlockInfoFromPos, useBlockNote} from './blocknote'
import {insertOrUpdateBlock} from './blocknote/core/extensions/SlashMenu/defaultSlashMenuItems'
import {FILE_DROP_INSERTED_EVENT} from './blocknote/core/extensions/DragMedia/DragExtension'
import {HyperMediaEditorView} from './editor-view'
import {createHypermediaDocLinkPlugin} from './hypermedia-link-plugin'
import {MobileSlashDialog} from './mobile-slash-dialog'
import {mentionSuggestionPluginKey} from './mention-suggestion-plugin'
import {slashMenuPluginKey} from './blocknote/core/extensions/SlashMenu/SlashMenuPlugin'
import {hmBlockSchema} from './schema'
import {getSlashMenuItems} from './slash-menu-items'
import {isMobileDevice, useMobile} from './use-mobile'
import {createMediaBlock, handleDragMedia, selectAllEditorContent, serverBlockNodesFromEditorBlocks} from './utils'

function crawlEditorBlocks(blocks: EditorBlock[], filter: (block: EditorBlock) => boolean): EditorBlock[] {
  const matchedChildren = blocks.flatMap((block) => crawlEditorBlocks(block.children, filter))
  return [...matchedChildren, ...blocks.filter(filter)]
}

function collectSerializedMediaRefs(blocks: EditorBlock[]) {
  const mediaRefs: Record<string, string> = {}
  const mediaBlocks = crawlEditorBlocks(blocks, (block) => !!(block.props as any)?.mediaRef)

  for (const block of mediaBlocks) {
    const mediaRef = (block.props as any).mediaRef
    mediaRefs[block.id] = typeof mediaRef === 'string' ? mediaRef : JSON.stringify(mediaRef)
  }

  return mediaRefs
}

const [setGwUrl, gwUrl] = writeableStateStream<string>('https://hyper.media')

export function useCommentEditor(
  perspectiveAccountUid?: string | null | undefined,
  onSubmit?: () => void,
  onMobileMentionTrigger?: () => void,
  onMobileSlashTrigger?: () => void,
  importWebFile?: (url: string) => Promise<{
    displaySrc: string
    fileBinary?: Uint8Array
    type: string
    size: number
  }>,
  handleFileAttachment?: (file: File) => Promise<{
    displaySrc: string
    url?: string
    mime?: string
    name?: string
    fileBinary?: Uint8Array
    mediaRef?: {
      draftId: string
      mediaId: string
      name: string
      mime: string
      size: number
    }
  }>,
  universalClient?: UniversalClient,
  // Resolver that maps a hostname (e.g. eric.vicenti.net) to its Seed account UID.
  // Required so URLs pasted into embed/link inputs inside a comment can be
  // resolved to hm:// references instead of erroring as "not a hypermedia link".
  domainResolver?: DomainResolverFn,
  disableTrailingNode?: boolean,
  submitOnEnter?: boolean,
  getExtraSlashMenuItems?: () => CommentEditorSlashMenuItem[],
) {
  // Use refs so extensions created once by useBlockNote can access the latest callbacks.
  // useBlockNote only creates the editor on the first render, but these callbacks may
  // change (e.g., isMobile starts false due to SSR-safe useMedia initialization, then
  // becomes true after layout effect).
  const onSubmitRef = useRef<(() => void) | undefined>(onSubmit)
  onSubmitRef.current = onSubmit
  const onMobileMentionTriggerRef = useRef(onMobileMentionTrigger)
  onMobileMentionTriggerRef.current = onMobileMentionTrigger
  const onMobileSlashTriggerRef = useRef(onMobileSlashTrigger)
  onMobileSlashTriggerRef.current = onMobileSlashTrigger
  const editorRef = useRef<BlockNoteEditor<typeof hmBlockSchema> | null>(null)
  const submitOnEnterRef = useRef(submitOnEnter)
  submitOnEnterRef.current = submitOnEnter
  // The slash menu reads items on every keystroke, so a ref keeps host-provided items current.
  const getExtraSlashMenuItemsRef = useRef(getExtraSlashMenuItems)
  getExtraSlashMenuItemsRef.current = getExtraSlashMenuItems

  const editor = useBlockNote<typeof hmBlockSchema>({
    onEditorContentChange(editor: BlockNoteEditor<typeof hmBlockSchema>) {
      // console.log("editor content changed", editor.topLevelBlocks);
    },
    disableTrailingNode,
    linkExtensionOptions: {
      openOnClick: false,
      universalClient,
      gwUrl,
      domainResolver,
      onPasteHypermediaBlockFragment: (resolvedHmUrl: string) => {
        const ed = editorRef.current
        if (!ed) return false
        insertOrUpdateBlock(
          ed,
          {
            type: 'embed',
            props: {url: resolvedHmUrl, view: 'Content'},
          } as any,
          true,
        )
        return true
      },
    },

    // onEditorReady: (e) => {
    //   readyEditor.current = e;
    //   initDraft();
    // },
    blockSchema: hmBlockSchema,
    getSlashMenuItems: () => [
      ...toBlockNoteSlashMenuItems(getExtraSlashMenuItemsRef.current?.() ?? []),
      ...getSlashMenuItems(),
    ],
    importWebFile,
    handleFileAttachment,
    _tiptapOptions: {
      extensions: [
        Extension.create({
          name: 'hypermedia-link',
          addProseMirrorPlugins() {
            return [createHypermediaDocLinkPlugin({domainResolver}).plugin]
          },
        }),
        Extension.create({
          name: 'comment-submit-shortcut',
          priority: 1000,
          addKeyboardShortcuts() {
            return {
              'Mod-a': ({editor}) => {
                return selectAllEditorContent(editor)
              },
              'Mod-Enter': () => {
                if (onSubmitRef.current) {
                  onSubmitRef.current()
                  return true
                }
                return false
              },
              Enter: ({editor}) => {
                if (!submitOnEnterRef.current) return false
                const slashState = slashMenuPluginKey.getState(editor.state) as {active?: boolean} | undefined
                const mentionState = mentionSuggestionPluginKey.getState(editor.state) as {active?: boolean} | undefined
                if (slashState?.active || mentionState?.active) return false
                if (onSubmitRef.current) {
                  onSubmitRef.current()
                  return true
                }
                return false
              },
            }
          },
        }),
        // Mobile-specific handlers for mentions and slash menu.
        Extension.create({
          name: 'mobile-dialog-triggers',
          priority: 2000,
          addProseMirrorPlugins() {
            return [
              new Plugin({
                key: new PluginKey('mobile-dialog-triggers'),
                props: {
                  handleKeyDown(view, event) {
                    if (!isMobileDevice()) return false

                    if (event.key === '/' && onMobileSlashTriggerRef.current) {
                      const {selection} = view.state
                      const $from = selection.$from
                      const textBeforeCursor = $from.parent.textContent.substring(0, $from.parentOffset)
                      const isAtStart = textBeforeCursor.length === 0
                      const isAfterSpace = textBeforeCursor.endsWith(' ')

                      if (isAtStart || isAfterSpace) {
                        onMobileSlashTriggerRef.current()
                        return true
                      }
                    }

                    return false
                  },
                },
              }),
            ]
          },
        }),
      ],
    },
  })

  editorRef.current = editor

  return {
    editor,
  }
}

export interface CommentEditorProps {
  draft: Array<HMBlockNode>
  onSubmit: (content: Array<HMBlockNode>) => Promise<void>
  onDelete: (draftKey: string) => Promise<void>
  onMedia: () => Promise<void>
  /** Focus the editor on mount. Renamed from `autoFocus` to avoid the `jsx-a11y/no-autofocus` rule; focus is driven imperatively via effect. */
  focusOnMount?: boolean
  signer?: HMMetadata
}

/**
 * CommentEditor
 * props:
 * - draft: editorDraft if any
 * - onSubmit: async function that submits the comment
 *   - content: editor content
 *   -
 * - focusOnMount?: if we need to focus the editor right away (imperatively)
 * - onMedia: function that handles how we process images
 * when paste or included
 * - signer: account metadata for the signer to render avatar and name
 * - onDelete: function. self explanatory
 *
 * - logic:
 *   - load editor data depending if there's a draft already
 * with content or not
 *   - add listener to editor update to save editor I guess.
 *     maybe we can just pass the safeDraft function and call it here
 *   - focus editor if needed (focusOnMount?)
 *   - getContent function is defined here
 *   - handle cmd+A when the editor is focused
 *   - pass the function to handle Images when pasted or upload
 *     and call it inside the editor extension
 *   - render the editor right away
 *   -
 *
 */

/** A host command in the comment editor's `/` menu. The typed `/query` is removed before
 * {@link CommentEditorSlashMenuItem.onSelect} runs. */
export type CommentEditorSlashMenuItem = {
  name: string
  aliases?: string[]
  group: string
  icon: JSX.Element
  hint?: string
  onSelect: () => void
}

function toBlockNoteSlashMenuItems(items: CommentEditorSlashMenuItem[]) {
  return items.map(({onSelect, ...item}) => ({...item, execute: () => onSelect()}))
}

export type CommentEditorSubmitHandle = {
  submit: () => void
  reset: () => void
  focus: (options?: {moveCursorToEnd?: boolean}) => void
  /** Emits any pending (debounced) onContentChange synchronously. */
  flush: () => void
  getContent: (
    prepareAttachments: (binaries: Uint8Array[]) => Promise<{
      blobs: {cid: string; data: Uint8Array}[]
      resultCIDs: string[]
    }>,
  ) => Promise<{
    blockNodes: HMBlockNode[]
    blobs: {cid: string; data: Uint8Array}[]
  }>
}

export function CommentEditor({
  submitButton,
  handleSubmit,
  account,
  focusOnMount,
  isReplying,
  perspectiveAccountUid,
  siteUid,
  documentId,
  mentionThread,
  referenceTarget,
  initialBlocks,
  onContentChange,
  onAvatarPress,
  importWebFile,
  handleFileAttachment,
  universalClient,
  getDraftMediaBlob,
  hideAvatar,
  compact = false,
  hideSubmitToolbar,
  submitHandleRef,
  disableTrailingNode,
  submitOnEnter,
  domainResolver,
  getExtraSlashMenuItems,
  collapsedLabel,
}: {
  submitButton: (opts: {
    reset: () => void
    disabled?: boolean
    getContent: (
      prepareAttachments: (binaries: Uint8Array[]) => Promise<{
        blobs: {cid: string; data: Uint8Array}[]
        resultCIDs: string[]
      }>,
    ) => Promise<{
      blockNodes: HMBlockNode[]
      blobs: {cid: string; data: Uint8Array}[]
    }>
  }) => JSX.Element
  handleSubmit: (
    getContent: (
      prepareAttachments: (binaries: Uint8Array[]) => Promise<{
        blobs: {cid: string; data: Uint8Array}[]
        resultCIDs: string[]
      }>,
    ) => Promise<{
      blockNodes: HMBlockNode[]
      blobs: {cid: string; data: Uint8Array}[]
    }>,
    reset: () => void,
  ) => void
  account?: ReturnType<typeof useAccount>['data']
  /** Focus the editor on mount, driven imperatively via effect. */
  focusOnMount?: boolean
  isReplying?: boolean
  perspectiveAccountUid?: string | null | undefined
  siteUid?: string
  documentId?: UnpackedHypermediaId
  /** Loaded participants in the conversation being replied to. */
  mentionThread?: MentionThreadContext
  /** Accept document references only for this active comment draft, never inline edits. */
  referenceTarget?: {documentId: string; commentId?: string}
  initialBlocks?: HMBlockNode[]
  onContentChange?: (blocks: HMBlockNode[], mediaRefs?: Record<string, string>) => void
  onAvatarPress?: () => void
  importWebFile?: (url: string) => Promise<{
    displaySrc: string
    fileBinary?: Uint8Array
    type: string
    size: number
  }>
  handleFileAttachment?: (file: File) => Promise<{
    displaySrc: string
    url?: string
    fileBinary?: Uint8Array
    mediaRef?: {
      draftId: string
      mediaId: string
      name: string
      mime: string
      size: number
    }
  }>
  getDraftMediaBlob?: (draftId: string, mediaId: string) => Promise<Blob | null>
  /** Render a compact field with its submit button outside the border. */
  compact?: boolean
  /** Hide the leading avatar */
  hideAvatar?: boolean
  /** Hide the built-in footer toolbar when submit controls are rendered outside the editor. */
  hideSubmitToolbar?: boolean
  /** Exposes the current submit/getContent/reset handles for external submit controls. */
  submitHandleRef?: MutableRefObject<CommentEditorSubmitHandle | null>
  /** Disables the editor's automatic empty trailing paragraph for compact chat composers. */
  disableTrailingNode?: boolean
  /** Submits on plain Enter while allowing mention/slash menus to handle Enter first. */
  submitOnEnter?: boolean
  /** Host commands listed ahead of the block types in the `/` menu. Read on every query, so the
   * list may change between renders. */
  getExtraSlashMenuItems?: () => CommentEditorSlashMenuItem[]
  /** Text on the collapsed field before it is first focused; "Start a Discussion" / "Write a
   * Reply" otherwise. */
  collapsedLabel?: string
  /** Optional resolver that maps a hostname to a Seed account UID, used when
   * pasting Hypermedia URLs in embed/link inputs nested inside this comment. */
  domainResolver?: DomainResolverFn
  universalClient?: UniversalClient
}) {
  const [submitTrigger, setSubmitTrigger] = useState(0)
  const submitCallbackRef = useRef<(() => void) | null>(null)
  const isMobile = useMobile()
  const [isSlashDialogOpen, setIsSlashDialogOpen] = useState(false)
  const toolbarRef = useRef<HTMLDivElement>(null)
  const contextUniversalClient = useUniversalClient()

  const {editor} = useCommentEditor(
    perspectiveAccountUid,
    () => setSubmitTrigger((prev) => prev + 1),
    undefined,
    isMobile ? () => setIsSlashDialogOpen(true) : undefined,
    importWebFile,
    handleFileAttachment,
    universalClient ?? contextUniversalClient,
    domainResolver,
    disableTrailingNode,
    submitOnEnter,
    getExtraSlashMenuItems,
  )
  // Check if we have non-empty draft content
  const hasDraftContent = blocksHaveDraftContent(initialBlocks)
  const [isExpanded, setIsExpanded] = useState(() => focusOnMount || hasDraftContent || false)
  const openUrl = useOpenUrl()
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const tx = useTx()
  const isInitializedRef = useRef(false)
  const [isDraftReady, setIsDraftReady] = useState(false)
  const draftReferences = useCommentDraftReferences()
  const insertedReferences = useRef(new WeakSet<CommentDraftReference>())
  const contentChangeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasLocalEditsRef = useRef(false)
  const isApplyingInitialBlocksRef = useRef(false)
  const shouldFocusOnActivateRef = useRef(false)
  const shouldMoveCursorToEndOnFocusRef = useRef(false)
  const dragDepthRef = useRef(0)
  const pendingDropRef = useRef<{
    files: File[]
    dropTarget: 'append' | {clientX: number; clientY: number}
  } | null>(null)

  const reset = () => {
    editor.removeBlocks(editor.topLevelBlocks)
  }

  const focusEditor = ({moveCursorToEnd = false}: {moveCursorToEnd?: boolean} = {}) => {
    if (moveCursorToEnd) {
      editor._tiptapEditor.chain().focus('end').run()
      return
    }
    editor._tiptapEditor.commands.focus()
  }

  const activateEditor = ({
    moveCursorToEnd = false,
    focusOnActivate = true,
  }: {
    moveCursorToEnd?: boolean
    focusOnActivate?: boolean
  } = {}) => {
    shouldFocusOnActivateRef.current = focusOnActivate
    shouldMoveCursorToEndOnFocusRef.current = moveCursorToEnd
    setIsExpanded(true)
  }

  const getAppendInsertionPos = () => {
    return Math.max(0, editor._tiptapEditor.view.state.doc.content.size - 4)
  }

  // Serializes the whole document for onContentChange. Too expensive to run
  // per keystroke (typing lags on long comments, #884), so changes only
  // schedule this and it runs debounced, or synchronously on blur/submit/unmount.
  const flushContentChange = () => {
    if (!contentChangeTimeoutRef.current) return
    clearTimeout(contentChangeTimeoutRef.current)
    contentChangeTimeoutRef.current = null
    if (!onContentChange) return

    try {
      // @ts-expect-error
      const editorBlocks: EditorBlock[] = editor.topLevelBlocks
      const mediaRefs = collectSerializedMediaRefs(editorBlocks)
      const blocks = serverBlockNodesFromEditorBlocks(editor, editorBlocks)
      onContentChange(
        blocks.map((b) => b.toJson()) as HMBlockNode[],
        Object.keys(mediaRefs).length > 0 ? mediaRefs : undefined,
      )
    } catch (error) {
      console.error('Failed to emit content change:', error)
    }
  }

  const insertDroppedFiles = async ({
    files,
    dropTarget,
  }: {
    files: File[]
    dropTarget: 'append' | {clientX: number; clientY: number}
  }) => {
    const ttEditor = editor._tiptapEditor
    let insertionPos = getAppendInsertionPos()

    if (dropTarget !== 'append') {
      const posAtCoords = ttEditor.view.posAtCoords({
        left: dropTarget.clientX,
        top: dropTarget.clientY,
      })
      if (posAtCoords && posAtCoords.inside !== -1) {
        insertionPos = posAtCoords.pos
      }
    }

    let lastId: string | undefined

    for (const file of files) {
      const props = await handleDragMedia(file, handleFileAttachment)
      const blockNode = createMediaBlock(file, props)
      if (!blockNode) continue

      if (lastId) {
        ;(editor as BlockNoteEditor).insertBlocks(
          // @ts-expect-error
          [blockNode],
          lastId,
          'after',
        )
        lastId = blockNode.id
        continue
      }

      const blockInfo = getBlockInfoFromPos(ttEditor.view.state, insertionPos)
      ;(editor as BlockNoteEditor).insertBlocks(
        // @ts-expect-error
        [blockNode],
        blockInfo.block.node.attrs.id,
        'after',
      )
      lastId = blockNode.id
    }

    flushContentChange()
  }

  const isFileDrag = (dataTransfer: DataTransfer | null | undefined) => {
    return !!dataTransfer && Array.from(dataTransfer.types || []).includes('Files')
  }

  const getDraggedFiles = (dataTransfer: DataTransfer | null | undefined) => {
    const files: File[] = []
    if (!dataTransfer) return files

    if (dataTransfer.files.length) {
      for (let i = 0; i < dataTransfer.files.length; i++) {
        const file = dataTransfer.files[i]
        if (file) files.push(file)
      }
      return files
    }

    for (let i = 0; i < dataTransfer.items.length; i++) {
      const dataItem = dataTransfer.items[i]
      if (!dataItem) continue
      const item = dataItem.getAsFile()
      if (item) files.push(item)
    }

    return files
  }

  const isPointInsideEditor = (clientX: number, clientY: number) => {
    const editorRect = editor._tiptapEditor.view.dom.getBoundingClientRect()
    return (
      clientX >= editorRect.left &&
      clientX <= editorRect.right &&
      clientY >= editorRect.top &&
      clientY <= editorRect.bottom
    )
  }

  // Initialize editor with draft content and rehydrate media
  useEffect(() => {
    if (!editor || isInitializedRef.current) return
    if (!initialBlocks?.length) {
      setIsDraftReady(true)
      return
    }
    if (hasLocalEditsRef.current) {
      isInitializedRef.current = true
      setIsDraftReady(true)
      return
    }

    isInitializedRef.current = true

    const initializeWithRehydration = async () => {
      try {
        const editorBlocks = hmBlocksToEditorContent(initialBlocks, {
          childrenType: 'Group',
        })

        // Rehydrate media blocks from IndexedDB
        if (getDraftMediaBlob) {
          const rehydrateEditorBlocks = async (blocks: any[]): Promise<void> => {
            for (const block of blocks) {
              if (
                (block.type === 'image' || block.type === 'video' || block.type === 'file') &&
                block.props?.mediaRef
              ) {
                // Parse mediaRef from JSON string
                let mediaRef
                try {
                  mediaRef =
                    typeof block.props.mediaRef === 'string' ? JSON.parse(block.props.mediaRef) : block.props.mediaRef
                } catch (e) {
                  console.error('Failed to parse mediaRef:', e)
                  continue
                }

                const {draftId, mediaId} = mediaRef
                try {
                  const blob = await getDraftMediaBlob(draftId, mediaId)
                  if (blob) {
                    // Clear any old url and set a new blob url
                    block.props.url = ''
                    block.props.displaySrc = URL.createObjectURL(blob)
                  } else {
                    console.warn(`Media blob not found in IndexedDB for rehydration: ${draftId}/${mediaId}`)
                  }
                } catch (error) {
                  console.error(`Failed to rehydrate media ${mediaId}:`, error)
                }
              }

              // Process children recursively
              if (block.children && block.children.length > 0) {
                await rehydrateEditorBlocks(block.children)
              }
            }
          }

          await rehydrateEditorBlocks(editorBlocks)
        }

        if (hasLocalEditsRef.current) {
          return
        }

        isApplyingInitialBlocksRef.current = true
        editor.removeBlocks(editor.topLevelBlocks)
        // @ts-expect-error - EditorBlock type mismatch with BlockNote
        editor.replaceBlocks(editor.topLevelBlocks, editorBlocks)
        isApplyingInitialBlocksRef.current = false
      } catch (error) {
        isApplyingInitialBlocksRef.current = false
        console.error('Failed to initialize editor with draft content:', error)
      } finally {
        setIsDraftReady(true)
      }
    }

    initializeWithRehydration()
  }, [initialBlocks, editor, getDraftMediaBlob])

  // Cleanup object URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Revoke any blob URLs in the editor blocks on unmount
      if (editor && typeof window !== 'undefined') {
        try {
          const blocks = editor.topLevelBlocks
          const revokeFromBlock = (block: any) => {
            if (
              block.props?.displaySrc &&
              typeof block.props.displaySrc === 'string' &&
              block.props.displaySrc.startsWith('blob:')
            ) {
              try {
                URL.revokeObjectURL(block.props.displaySrc)
              } catch (error) {
                // URL might already be revoked
              }
            }
            if (block.children && Array.isArray(block.children)) {
              block.children.forEach(revokeFromBlock)
            }
          }
          blocks.forEach(revokeFromBlock)
        } catch (error) {
          // Editor might be in invalid state during unmount
          console.debug('Failed to revoke URLs on unmount:', error)
        }
      }
    }
  }, [editor])

  // Notify parent of content changes, debounced (see flushContentChange)
  useEffect(() => {
    if (!onContentChange) return

    const handleChange = () => {
      if (!isApplyingInitialBlocksRef.current) {
        hasLocalEditsRef.current = true
      }
      if (contentChangeTimeoutRef.current) {
        clearTimeout(contentChangeTimeoutRef.current)
      }
      contentChangeTimeoutRef.current = setTimeout(flushContentChange, 300)
    }

    editor._tiptapEditor.on('update', handleChange)
    // Flush when focus leaves the editor (e.g. tapping a save/submit button)
    editor._tiptapEditor.on('blur', flushContentChange)

    return () => {
      editor._tiptapEditor.off('update', handleChange)
      editor._tiptapEditor.off('blur', flushContentChange)
      flushContentChange()
    }
  }, [editor, onContentChange])

  useEffect(() => {
    if (!referenceTarget || !draftReferences || !isDraftReady) return
    const pending = draftReferences.references.filter(
      (reference) =>
        reference.documentId === referenceTarget.documentId &&
        reference.commentId === referenceTarget.commentId &&
        !insertedReferences.current.has(reference),
    )
    if (!pending.length) return
    if (!isExpanded) {
      setIsExpanded(true)
      return
    }
    let didInsert = false
    for (const reference of pending) {
      // A URL can be opened again after this editor remounts with its saved draft.
      // Consume that request without duplicating an embed already in the content.
      const existing = crawlEditorBlocks(
        editor.topLevelBlocks as EditorBlock[],
        (block) => block.type === 'embed' && block.props.url === reference.link,
      )
      if (existing.length) {
        insertedReferences.current.add(reference)
        draftReferences.consumeReference(reference)
        continue
      }
      const lastBlock = editor.topLevelBlocks.at(-1)
      if (!lastBlock) continue
      const embed = {type: 'embed' as const, props: {url: reference.link, view: 'Content' as const}}
      if (lastBlock.type === 'paragraph' && !lastBlock.content?.length && !lastBlock.children.length) {
        // Reuse the editor's empty trailing paragraph as the new typing position.
        editor.insertBlocks([embed], lastBlock, 'before')
      } else {
        editor.insertBlocks([embed, {type: 'paragraph', content: []}], lastBlock, 'after')
      }
      insertedReferences.current.add(reference)
      draftReferences.consumeReference(reference)
      didInsert = true
    }
    if (!didInsert) return
    hasLocalEditsRef.current = true
    flushContentChange()
    editor._tiptapEditor.commands.focus('end')
  }, [draftReferences, referenceTarget?.documentId, referenceTarget?.commentId, isDraftReady, isExpanded, editor])

  useEffect(() => {
    if (hasDraftContent && !isExpanded) {
      shouldFocusOnActivateRef.current = false
      shouldMoveCursorToEndOnFocusRef.current = false
      setIsExpanded(true)
    }
  }, [hasDraftContent, isExpanded])

  useEffect(() => {
    if (focusOnMount) {
      setIsExpanded(true)
      shouldFocusOnActivateRef.current = true
    }
  }, [focusOnMount])

  useLayoutEffect(() => {
    if (!isExpanded || !shouldFocusOnActivateRef.current) return
    shouldFocusOnActivateRef.current = false
    focusEditor({moveCursorToEnd: shouldMoveCursorToEndOnFocusRef.current})
    shouldMoveCursorToEndOnFocusRef.current = false
  }, [editor, isExpanded])

  useEffect(() => {
    if (!isExpanded || !pendingDropRef.current) return

    const pendingDrop = pendingDropRef.current
    pendingDropRef.current = null
    const frameId = requestAnimationFrame(() => {
      void insertDroppedFiles(pendingDrop)
    })

    return () => cancelAnimationFrame(frameId)
  }, [editor, isExpanded])

  useEffect(() => {
    if (!onContentChange) return

    const editorDom = editor._tiptapEditor.view.dom
    const handleInsertedFileDrop = () => {
      flushContentChange()
    }

    editorDom.addEventListener(FILE_DROP_INSERTED_EVENT, handleInsertedFileDrop)

    return () => {
      editorDom.removeEventListener(FILE_DROP_INSERTED_EVENT, handleInsertedFileDrop)
    }
  }, [editor, onContentChange])

  // Handle mobile keyboard - scroll toolbar into view when keyboard appears
  useLayoutEffect(() => {
    if (!isMobile || !isExpanded || !toolbarRef.current) return
    if (typeof window === 'undefined' || !window.visualViewport) return

    const viewport = window.visualViewport
    let initialHeight = viewport.height

    const handleViewportResize = () => {
      // Detect keyboard opening (viewport shrinks significantly)
      const heightDiff = initialHeight - viewport.height
      if (heightDiff > 100 && toolbarRef.current) {
        // Keyboard opened - scroll toolbar into view
        toolbarRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }
    }

    // Also scroll into view when editor first gets focused
    const scrollTimeout = setTimeout(() => {
      if (toolbarRef.current) {
        toolbarRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        })
      }
    }, 300)

    viewport.addEventListener('resize', handleViewportResize)

    return () => {
      clearTimeout(scrollTimeout)
      viewport.removeEventListener('resize', handleViewportResize)
    }
  }, [isMobile, isExpanded])

  const getContent = async (
    prepareAttachments: (binaries: Uint8Array[]) => Promise<{
      blobs: {cid: string; data: Uint8Array}[]
      resultCIDs: string[]
    }>,
  ) => {
    // Emit any debounced content change before the destructive mutations below,
    // so parents (draft persistence) hold the final pre-publish content.
    flushContentChange()

    // @ts-expect-error
    const editorBlocks: EditorBlock[] = editor.topLevelBlocks

    // Collect blocks with fileBinary or mediaRef
    const blocksWithAttachments = crawlEditorBlocks(
      editorBlocks,
      // @ts-expect-error
      (block) => !!block.props?.fileBinary || !!block.props?.mediaRef,
    )

    // Prepare binaries for upload
    // TODO: decide if this should be removed
    const binariesToUpload: Uint8Array[] = []
    const blockToIndexMap = new Map<any, number>()

    for (const block of blocksWithAttachments) {
      // @ts-expect-error
      if (block.props?.fileBinary) {
        blockToIndexMap.set(block, binariesToUpload.length)
        // @ts-expect-error
        binariesToUpload.push(block.props.fileBinary)
      }
      // @ts-expect-error
      else if (block.props?.mediaRef && getDraftMediaBlob) {
        // Parse mediaRef from JSON string
        let mediaRef
        try {
          mediaRef =
            // @ts-expect-error - mediaRef exists on media blocks
            typeof block.props.mediaRef === 'string'
              ? // @ts-expect-error
                JSON.parse(block.props.mediaRef)
              : // @ts-expect-error
                block.props.mediaRef
        } catch (e) {
          console.error('Failed to parse mediaRef:', e)
          continue
        }

        const {draftId, mediaId} = mediaRef
        try {
          const blob = await getDraftMediaBlob(draftId, mediaId)
          if (blob) {
            const arrayBuffer = await blob.arrayBuffer()
            const binary = new Uint8Array(arrayBuffer)
            blockToIndexMap.set(block, binariesToUpload.length)
            binariesToUpload.push(binary)
          } else {
            console.warn(`Media not found: ${draftId}/${mediaId}`)
          }
        } catch (error) {
          console.error('Failed to load media:', error)
        }
      }
    }
    const {blobs, resultCIDs} = await prepareAttachments(binariesToUpload)

    // Update blocks with IPFS URLs and clean up temporary properties
    blocksWithAttachments.forEach((block) => {
      const index = blockToIndexMap.get(block)
      if (index !== undefined) {
        // @ts-expect-error
        block.props.url = `ipfs://${resultCIDs[index]}`
      } else {
        // Clear the block if failed to retrieve blob from IndexedDB. This avoids publishing corrupt image.
        console.error(`Cannot publish block ${block.id} - media not found in IndexedDB`)
        // @ts-expect-error
        block.props.url = ''
      }

      // Reset temporary properties to defaults instead of deleting,
      // because delete can leave undefined values that break CBOR encoding
      // @ts-expect-error
      block.props.fileBinary = ''
      // @ts-expect-error
      block.props.mediaRef = ''
      // @ts-expect-error
      block.props.displaySrc = ''
    })

    const blocks = serverBlockNodesFromEditorBlocks(editor, editorBlocks)
    const blockNodes = blocks.map((b) => b.toJson()) as HMBlockNode[]
    return {
      blockNodes,
      blobs,
    }
  }

  const handleImageClick = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*,.heic,.heif,image/heic,image/heif,video/*'
    input.multiple = true

    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || [])
      if (files.length === 0) return

      const ttEditor = editor._tiptapEditor
      const pos = ttEditor.view.state.doc.content.size - 4

      for (const file of files) {
        const props = await handleDragMedia(file, handleFileAttachment)
        const blockNode = createMediaBlock(file, props)
        if (!blockNode) continue

        const blockInfo = getBlockInfoFromPos(ttEditor.view.state, pos)
        editor.insertBlocks(
          // @ts-expect-error
          [blockNode],
          blockInfo.block.node.attrs.id,
          'after',
        )
      }
    }

    input.click()
  }

  // Keep callback ref updated with latest getContent and reset
  submitCallbackRef.current = () => {
    editor._tiptapEditor.commands.blur()
    handleSubmit(getContent, reset)
  }
  if (submitHandleRef) {
    submitHandleRef.current = {
      submit: () => submitCallbackRef.current?.(),
      reset,
      focus: focusEditor,
      flush: flushContentChange,
      getContent,
    }
  }

  // Handle submit triggered by keyboard shortcut
  useEffect(() => {
    if (submitTrigger > 0) {
      submitCallbackRef.current?.()
    }
  }, [submitTrigger])

  useEffect(() => {
    return () => {
      if (submitHandleRef) submitHandleRef.current = null
    }
  }, [submitHandleRef])

  function onDrop(event: React.DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event.dataTransfer)) return false

    const isDropInsideEditor = isExpanded && isPointInsideEditor(event.clientX, event.clientY)
    if (isDropInsideEditor) {
      dragDepthRef.current = 0
      setIsDraggingOver(false)
      return false
    }

    event.preventDefault()
    event.stopPropagation()

    dragDepthRef.current = 0
    setIsDraggingOver(false)

    const files = getDraggedFiles(event.dataTransfer)
    if (!files.length) return false

    if (!isExpanded) {
      pendingDropRef.current = {files, dropTarget: 'append'}
      activateEditor({focusOnActivate: false})
      return true
    }

    void insertDroppedFiles({files, dropTarget: 'append'})
    return true
  }

  if (!editor) {
    console.error('CommentEditor: editor is null/undefined')
    return (
      <div className="border-destructive bg-destructive/10 rounded border p-4">
        <p className="text-destructive text-sm">Error: Editor failed to initialize. Check console for details.</p>
      </div>
    )
  }

  return (
    <>
      <div className="flex w-full items-start gap-2">
        {hideAvatar || compact ? null : (
          <div className="flex shrink-0 grow-0">
            {account?.metadata ? (
              <LinkIcon id={account.id} metadata={account.metadata} size={32} />
            ) : (
              <UIAvatar url={avatarPlaceholder} size={32} onPress={onAvatarPress} className="rounded-full" />
            )}
          </div>
        )}
        <div
          className={cn(
            'bg-muted border-border dark:border-muted-foreground/20 focus-within:border-ring/60 w-full min-w-0 flex-1 rounded-lg border transition-[filter,border-color,background-color]',
            compact && 'bg-card rounded-xl',
            isExpanded
              ? ''
              : 'hover:border-black/10 hover:brightness-[1.01] active:brightness-95 dark:hover:border-white/10',
            isDraggingOver &&
              'border-dashed border-black/20 bg-black/[0.03] brightness-100 dark:border-white/20 dark:bg-white/[0.04]',
          )}
          onDragEnter={(event) => {
            if (!isFileDrag(event.dataTransfer)) return
            if (isExpanded && isPointInsideEditor(event.clientX, event.clientY)) {
              dragDepthRef.current = 0
              setIsDraggingOver(false)
              return
            }
            event.preventDefault()
            dragDepthRef.current += 1
            setIsDraggingOver(true)
          }}
          onDragLeave={(event) => {
            if (!isFileDrag(event.dataTransfer)) return
            if (isExpanded && isPointInsideEditor(event.clientX, event.clientY)) {
              return
            }
            event.preventDefault()
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
            if (dragDepthRef.current === 0) {
              setIsDraggingOver(false)
            }
          }}
          onDragOver={(event) => {
            if (!isFileDrag(event.dataTransfer)) return
            if (isExpanded && isPointInsideEditor(event.clientX, event.clientY)) {
              if (isDraggingOver) {
                dragDepthRef.current = 0
                setIsDraggingOver(false)
              }
              return
            }
            event.preventDefault()
            event.dataTransfer.dropEffect = 'copy'
            setIsDraggingOver(true)
          }}
          onDrop={onDrop}
          onMouseDown={(e) => {
            const target = e.target as HTMLElement

            if (
              target.closest(
                'button, input, textarea, select, a, [role="button"], .ProseMirror, [contenteditable="true"]',
              )
            ) {
              return
            }

            e.preventDefault()
            e.stopPropagation()

            if (isExpanded) {
              focusEditor({moveCursorToEnd: true})
              return
            }

            activateEditor({moveCursorToEnd: true})
          }}
        >
          <div
            className={cn(
              'hm-prose is-comment comment-editor w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto',
              compact ? 'max-h-40 min-h-12' : 'max-h-[160px] min-h-20 md:max-h-full',
              isExpanded
                ? 'justify-start px-3 pt-1 pb-2'
                : compact
                  ? 'flex items-center justify-center'
                  : 'justify-center',
            )}
            // marginTop="$1"

            // minHeight={isExpanded ? 105 : 40}
            // paddingHorizontal="$4"
            onClick={(e) => {
              const target = e.target as HTMLElement

              // Check if the clicked element is not an input, button, or textarea
              if (target.closest('input, textarea, select, button')) {
                return // Don't focus the editor in this case
              }
              e.stopPropagation()
              if (isExpanded) {
                focusEditor()
                return
              }
              activateEditor()
            }}
          >
            {isExpanded ? (
              <HyperMediaEditorView
                editor={editor}
                openUrl={openUrl}
                perspectiveAccountUid={perspectiveAccountUid}
                mentionThread={mentionThread}
                siteUid={siteUid}
                documentId={documentId}
              />
            ) : (
              <Button
                onClick={() => {
                  activateEditor()
                }}
                className={cn(
                  'text-muted-foreground m-0 h-auto min-h-8 w-full flex-1 items-center justify-start border-0 text-left text-base hover:bg-transparent focus:bg-transparent',
                  'plausible-event-name=Comment+Box+Click',
                )}
                variant="ghost"
                size="sm"
              >
                {collapsedLabel ?? tx(isReplying ? 'Write a Reply' : 'Start a Discussion')}
              </Button>
            )}
          </div>
          {!hideSubmitToolbar && (!compact || (isExpanded && isMobile)) ? (
            <div ref={toolbarRef} className={cn('mx-2 mb-2 flex gap-2', isMobile ? 'justify-between' : 'justify-end')}>
              {isExpanded && isMobile && (
                <div className="flex items-center gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    aria-label="Mention account"
                    onClick={() => editor.mentionMenu?.open('account')}
                  >
                    <AtSignIcon className="size-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-8"
                    aria-label="Link document"
                    onClick={() => editor.mentionMenu?.open('document')}
                  >
                    [[
                  </Button>

                  <Button size="icon" variant="ghost" className="size-8" onClick={handleImageClick}>
                    <ImageIcon className="size-4" />
                  </Button>

                  <Button size="icon" variant="ghost" className="size-8" onClick={() => setIsSlashDialogOpen(true)}>
                    <SlashSquareIcon className="size-4" />
                  </Button>
                </div>
              )}

              {!compact && <div className="flex gap-2">{submitButton({reset, disabled: !isExpanded, getContent})}</div>}
            </div>
          ) : null}
        </div>
        {compact && !hideSubmitToolbar && submitButton({reset, disabled: !isExpanded, getContent})}
      </div>

      {/* Mobile dialogs */}
      {isMobile && (
        <>
          <MobileSlashDialog
            isOpen={isSlashDialogOpen}
            onClose={() => setIsSlashDialogOpen(false)}
            editor={editor}
            extraItems={toBlockNoteSlashMenuItems(getExtraSlashMenuItems?.() ?? [])}
          />
        </>
      )}
    </>
  )
}
